import type { TFunction } from 'i18next';
import type { Arealplan, Dispensasjon, DispensasjonInput, DispensasjonType, DispensasjonFra } from '@origo/planregister';
import type { DispensationGroup, DispensationItem, DispensasjonFormValues } from '../types';
import { dispensasjonTypeLabel, dispensasjonFraLabel } from '../codelists/dispensasjonType';
import { formatDateShortNb } from './formatDate';
import { parseIsoDate, toIsoDate } from './toProcessings';

/** `"ÅÅÅÅ/NNN"` for et gitt saksår/sakssekvensnummer-par, tom streng hvis mangler. */
function formatSaksnummer(saksaar?: number, sakssekvensnummer?: number): string {
    return saksaar !== undefined && sakssekvensnummer !== undefined
        ? `${saksaar}/${String(sakssekvensnummer).padStart(3, '0')}`
        : '';
}

function toDispensationItem(d: Dispensasjon, t: TFunction, documentCount = 0): DispensationItem {
    const beskrivelse = d.beskrivelse?.trim() || undefined;
    return {
        id: d.dispensasjonId,
        objectId: d.objectId,
        tittel: dispensasjonTypeLabel(d.dispensasjonType, t) || t('dispensations.untitled'),
        saksnummer: formatSaksnummer(d.saksaar, d.sakssekvensnummer),
        dato: formatDateShortNb(d.vedtaksdato),
        dispensasjonTypeLabel: dispensasjonTypeLabel(d.dispensasjonType, t),
        dispensasjonFraLabel: dispensasjonFraLabel(d.dispensasjonFra, t),
        varighet: d.varigDispensasjon
            ? t('dispensations.detail.permanent')
            : [formatDateShortNb(d.varighetFra), formatDateShortNb(d.varighetTil)].filter(Boolean).join(' – '),
        begrunnelse: d.begrunnelse?.trim() || undefined,
        beskrivelse,
        documentCount,
        values: toDispensasjonFormValues(d),
    };
}

/** Domene → skjemaverdier for redigering. Speilbilde av `toDispensasjonInput`. */
export function toDispensasjonFormValues(d: Dispensasjon): DispensasjonFormValues {
    return {
        dispensasjonType: d.dispensasjonType ?? '',
        dispensasjonFra: d.dispensasjonFra ?? '',
        vedtaksdato: toIsoDate(d.vedtaksdato),
        innvilgetDispensasjon: d.innvilgetDispensasjon,
        varigDispensasjon: d.varigDispensasjon,
        varighetFra: toIsoDate(d.varighetFra),
        varighetTil: toIsoDate(d.varighetTil),
        saksnummer: formatSaksnummer(d.saksaar, d.sakssekvensnummer),
        begrunnelse: d.begrunnelse ?? '',
        beskrivelse: d.beskrivelse ?? '',
    };
}

/** Skjema → oppdatert dispensasjon. Bevarer id-er, plan-tilknytning og felter
 *  utenfor skjemaet (kommunenummer, planidentifikasjon, geometry) fra original. */
export function applyDispensasjonFormToDispensasjon(
    original: Dispensasjon,
    values: DispensasjonFormValues
): Dispensasjon {
    const { saksaar, sakssekvensnummer } = parseSaksnummer(values.saksnummer);
    return {
        ...original,
        dispensasjonType: (values.dispensasjonType || undefined) as DispensasjonType | undefined,
        dispensasjonFra: (values.dispensasjonFra || undefined) as DispensasjonFra | undefined,
        vedtaksdato: parseIsoDate(values.vedtaksdato)!,
        innvilgetDispensasjon: values.innvilgetDispensasjon,
        varigDispensasjon: values.varigDispensasjon,
        varighetFra: values.varigDispensasjon ? undefined : parseIsoDate(values.varighetFra),
        varighetTil: values.varigDispensasjon ? undefined : parseIsoDate(values.varighetTil),
        saksaar,
        sakssekvensnummer,
        begrunnelse: values.begrunnelse || undefined,
        beskrivelse: values.beskrivelse || undefined,
    };
}

export function toDispensationGroups(
    dispensasjoner: Dispensasjon[],
    t: TFunction,
    documentCounts: ReadonlyMap<string, number> = new Map()
): DispensationGroup[] {
    const approved: DispensationItem[] = [];
    const rejected: DispensationItem[] = [];
    for (const d of dispensasjoner) {
        const item = toDispensationItem(d, t, documentCounts.get(d.dispensasjonId) ?? 0);
        if (d.innvilgetDispensasjon) approved.push(item);
        else rejected.push(item);
    }
    return [
        { key: 'approved', count: approved.length, items: approved },
        { key: 'rejected', count: rejected.length, items: rejected },
    ];
}

/** `defaults.saksnummer` (typisk planens saksnummer) brukes bare når det matcher
 *  `"ÅÅÅÅ/NNN"` — planens saksnummer er fritekst, og et ugyldig forhåndsutfylt
 *  felt ville gitt en valideringsfeil brukeren ikke selv har forårsaket. */
export function emptyDispensasjonForm(defaults: { saksnummer?: string } = {}): DispensasjonFormValues {
    const saksnummer = defaults.saksnummer?.trim() ?? '';
    return {
        dispensasjonType: '',
        dispensasjonFra: '',
        vedtaksdato: '',
        innvilgetDispensasjon: false,
        varigDispensasjon: true,
        varighetFra: '',
        varighetTil: '',
        saksnummer: SAKSNUMMER_PATTERN.test(saksnummer) ? saksnummer : '',
        begrunnelse: '',
        beskrivelse: '',
    };
}

const SAKSNUMMER_PATTERN = /^(\d{4})\/(\d+)$/;

/** Parser `"ÅÅÅÅ/NNN"` (jf. visningsformatet i `toDispensationItem`). Ugyldig
 *  eller tomt format gir `undefined` for begge feltene. */
function parseSaksnummer(value: string): { saksaar?: number; sakssekvensnummer?: number } {
    const match = SAKSNUMMER_PATTERN.exec(value.trim());
    if (!match) return {};
    return { saksaar: Number(match[1]), sakssekvensnummer: Number(match[2]) };
}

/** Sjekker om et saksnummer er tomt (gyldig, valgfritt felt) eller matcher `"ÅÅÅÅ/NNN"`. */
export function isValidSaksnummer(value: string): boolean {
    const trimmed = value.trim();
    return trimmed === '' || SAKSNUMMER_PATTERN.test(trimmed);
}

export function toDispensasjonInput(
    values: DispensasjonFormValues,
    napId: string,
    plan?: Arealplan | null
): DispensasjonInput {
    const { saksaar, sakssekvensnummer } = parseSaksnummer(values.saksnummer);
    return {
        dispensasjonId: crypto.randomUUID(),
        nasjonalArealplanId: napId,
        kommunenummer: plan?.admomrkode,
        planidentifikasjon: plan?.planId,
        dispensasjonType: (values.dispensasjonType || undefined) as DispensasjonType | undefined,
        dispensasjonFra: (values.dispensasjonFra || undefined) as DispensasjonFra | undefined,
        vedtaksdato: parseIsoDate(values.vedtaksdato)!,
        innvilgetDispensasjon: values.innvilgetDispensasjon,
        varigDispensasjon: values.varigDispensasjon,
        varighetFra: values.varigDispensasjon ? undefined : parseIsoDate(values.varighetFra),
        varighetTil: values.varigDispensasjon ? undefined : parseIsoDate(values.varighetTil),
        saksaar,
        sakssekvensnummer,
        begrunnelse: values.begrunnelse || undefined,
        beskrivelse: values.beskrivelse || undefined,
    };
}
